<script lang="ts">
  import { createContext } from 'reka-ui'

  export interface GatDialogProps {
    title: string
    icon?: IconValues
    iconClass?: string
    description?: string | ((...args: any[]) => string)
    buttons?: {
      ok?: (GatButtonProps & { onClick?: () => void }) | false
      cancel?: (GatButtonProps & { onClick?: () => void }) | false
    }
    loading?: boolean
    /** If the dialog should be closed when the user clicks outside the component. Is false by default */
    dismissable?: boolean
    /** If the dialog should show top-right close icon. Is true by default. */
    showCloseIcon?: boolean
    noFooter?: boolean
  }

  export interface GatDialogContext {
    /** If the dialog is provided. Used by sub-components to check if the dialog is provided. */
    provided: boolean
  }

  const [_useGatDialog, provideGatDialog] =
    createContext<GatDialogContext>('gs-gat-dialog')

  export function useGatDialogContext() {
    try {
      return _useGatDialog()
    } catch (error) {
      return {
        provided: false
      }
    }
  }
</script>

<script setup lang="ts">
  import {
    useUnsavedChangesModelValue,
    type UnsavedChangesOptions
  } from '@gat-ship/lib/router'
  import { icons, type IconValues } from '@gat-ship/icons'
  import { globalUtils } from '@gat-ship/utils'
  import { computed, useSlots, type Ref, onMounted, reactive, watch } from 'vue'
  import { useDialogZIndex } from '../../composables/useDialogZIndex'
  import gatButton, { type GatButtonProps } from '../gat-button/gat-button.vue'
  import gatIcon from '../gat-icon/gat-icon.vue'
  import {
    Dialog,
    DialogTrigger,
    DialogTitle,
    DialogDescription,
    DialogScrollContent
  } from '../ui/dialog'
  import { testIds } from '@gat-ship/types'

  defineOptions({ inheritAttrs: false })

  const props = withDefaults(defineProps<GatDialogProps>(), {
    showCloseIcon: undefined,
    noFooter: false
  })

  const isLoading = computed(() => props.loading)

  const errorMessage = defineModel<string | undefined>('errorMessage', {
    default: undefined
  })

  provideGatDialog({
    provided: true
  })

  const hasEvent = reactive({ clickCancel: false, clickSave: false })

  onMounted(() => {
    hasEvent.clickCancel =
      globalUtils.functions.components.hasEmitListener('click:cancel')
    hasEvent.clickSave =
      globalUtils.functions.components.hasEmitListener('click:save')
  })

  const isOpen = defineModel('open', { default: false })

  const slots = useSlots()

  const emit = defineEmits<{
    (e: 'update:open', openState: boolean): void
    (e: 'click:save'): void
    (e: 'click:cancel'): void
  }>()

  const okButtonProps = computed(
    (): (GatButtonProps & { onClick?: () => void }) | undefined => {
      if (props.buttons?.ok === false) {
        return undefined
      }
      if (props.buttons?.ok) {
        return props.buttons.ok
      }
      if (hasEvent.clickSave) {
        return {
          text: 'Save Changes'
        }
      }
      return undefined
    }
  )
  const cancelButtonProps = computed(
    (): (GatButtonProps & { onClick?: () => void }) | undefined => {
      if (props.buttons?.cancel === false) {
        return undefined
      }
      if (props.buttons?.cancel !== undefined) {
        return props.buttons.cancel
      }
      return {
        text: 'Cancel',
        variant: 'ghost'
      }
    }
  )

  function cancelClicked() {
    if (hasEvent.clickCancel) {
      emit('click:cancel')
    } else if (cancelButtonProps.value?.onClick) {
      if (Array.isArray(cancelButtonProps.value?.onClick)) {
        cancelButtonProps.value.onClick.forEach((e) => e())
      } else {
        cancelButtonProps.value.onClick()
      }
    } else {
      emit('update:open', false)
    }
  }

  const unsavedChangesOptions = reactive<UnsavedChangesOptions>({
    preventTabClose: false,
    preventNavigationWhenOpen: true
  })

  function setUnsavedChangesOptions(options: Partial<UnsavedChangesOptions>) {
    if (options.preventTabClose !== undefined) {
      unsavedChangesOptions.preventTabClose = options.preventTabClose
    }
    if (options.preventNavigationWhenOpen !== undefined) {
      unsavedChangesOptions.preventNavigationWhenOpen =
        options.preventNavigationWhenOpen
    }
  }

  const { setState, hasState, hasChanges } = useUnsavedChangesModelValue(
    isOpen,
    unsavedChangesOptions
  )

  const okButtonIsDisabled = computed(() => {
    if (props.buttons?.ok && props.buttons.ok.disabled) {
      return props.buttons.ok.disabled
    }
    if (hasState.value) {
      return !hasChanges.value
    }

    return false
  })

  function getIconClass() {
    // We don't want to include w- classes and bg- classes to the icon.
    const iconClass = props.iconClass
      ?.split(' ')
      .filter((className) => !className.startsWith('w-'))
      .filter((className) => !className.startsWith('bg-'))
    // We only want to include the classes specified in the filterFn
    const iconContainerClass = props.iconClass
      ?.split(' ')
      .filter((className) => className.startsWith('bg-'))
    return {
      iconClass,
      iconContainerClass
    }
  }

  function onOutsideClick(event: Event) {
    event.preventDefault()
    if (props.dismissable === true) {
      isOpen.value = false
    }
  }

  watch(isOpen, (newVal) => {
    if (!newVal) {
      errorMessage.value = undefined
      document.removeEventListener('keydown', handleOkClicked)
    } else {
      document.addEventListener('keydown', handleOkClicked)
    }
  })

  defineExpose({
    setDialogDataState: setState,
    setUnsavedChangesOptions,
    setErrorMessage: (message: string) => {
      errorMessage.value = message
    },
    clearErrorMessage: () => {
      errorMessage.value = undefined
    }
  })

  function handleOkClicked(event: KeyboardEvent) {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault()
      event.stopPropagation()
      if (okButtonProps.value?.onClick) {
        okButtonProps.value.onClick()
      }
      emit('click:save')
    }
  }

  const { zIndex } = useDialogZIndex(isOpen)
</script>
<template>
  <Dialog v-model:open="isOpen" :modal="true">
    <DialogTrigger v-if="slots.trigger" as-child>
      <slot name="trigger" />
    </DialogTrigger>
    <DialogScrollContent
      :overlay-z-index="zIndex"
      :class="[
        $attrs.class,
        'flex w-fit min-w-[350px] shrink flex-col gap-0 rounded-lg p-0 shadow-lg'
      ]"
      :show-close-icon
      @interact-outside="onOutsideClick"
    >
      <div class="p-5">
        <div class="flex w-full items-center gap-2">
          <div
            v-if="icon"
            :class="[
              'flex h-10 w-10 items-center justify-center rounded-md border',
              getIconClass().iconContainerClass
            ]"
          >
            <gat-icon
              v-if="!isLoading"
              :icon
              :stroke-width="1.5"
              :class="[getIconClass().iconClass]"
            />
            <spinner v-else />
          </div>
          <div class="flex flex-col" :class="description ? 'gap-1.5' : ''">
            <DialogTitle class="text-lg font-semibold leading-none">
              {{ title }}
            </DialogTitle>
            <DialogDescription class="text-soft text-sm leading-none">
              {{
                typeof description === 'function' ? description() : description
              }}
            </DialogDescription>
          </div>
        </div>
      </div>
      <div class="flex-1">
        <div
          v-if="errorMessage"
          class="text-destructive border-destructive bg-destructive/10 m-2 flex items-center gap-2 rounded-md border p-4 text-xs"
        >
          <div>
            <gat-icon :icon="icons.common.error" />
          </div>
          <div>
            <div>Oops! Something went wrong...</div>
            <div class="text-sm font-medium">
              {{ errorMessage }}
            </div>
          </div>
        </div>
        <slot v-else />
      </div>
      <slot
        v-if="
          !noFooter && ($slots.footer || okButtonProps || cancelButtonProps)
        "
        name="footer"
      >
        <div class="flex flex-col">
          <div class="ml-auto flex gap-2 p-5">
            <gat-button
              v-if="cancelButtonProps"
              v-bind="cancelButtonProps"
              @click="cancelClicked()"
            />
            <gat-button
              v-if="okButtonProps"
              v-bind="okButtonProps"
              :label="okButtonProps.label ?? 'Save Changes'"
              :disabled="okButtonIsDisabled"
              :data-testid="
                okButtonProps?.testId ?? testIds.common.dialog.saveButton
              "
              @click="[emit('click:save'), okButtonProps.onClick]"
            />
          </div>
        </div>
      </slot>
    </DialogScrollContent>
  </Dialog>
</template>
